import SimpleBar from 'simplebar';

const API_CONFIG = {
    baseUrl: '/wp-json/pozamezh/v1'
};

const monthNames = {
    uk: [
        'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
        'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
    ]
};

class BookingCalendar {
    constructor() {
        const now = new Date();
        
        this.currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
        this.calendarBody = document.getElementById('calendarBody');
        this.monthText = document.querySelector('.calendar-nav__month-text');
        this.prevBtn = document.querySelector('.calendar-nav__btn--prev');
        this.nextBtn = document.querySelector('.calendar-nav__btn--next');
        this.isLoading = false;
        this.monthDataCache = new Map();
        
        this.showLoader();
        this.init();
    }
    
    async initializeDate() {
        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        
        const hasCurrentMonthData = await this.hasMonthData(currentMonth);
        
        if (hasCurrentMonthData) {
            this.currentDate = currentMonth;
        } else {
            const firstAvailable = await this.findFirstAvailableMonth(now);
            this.currentDate = firstAvailable;
        }
    }
    
    
    async findFirstAvailableMonth(startDate) {
        let testDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const maxMonths = 24;
        
        for (let i = 0; i < maxMonths; i++) {
            if (await this.hasMonthData(testDate)) {
                return testDate;
            }
            testDate = new Date(testDate.getFullYear(), testDate.getMonth() + 1, 1);
        }
        
        return new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    }
    
    async init() {
        await this.initializeDate();
        this.monthText.textContent = this.getMonthName();
        await this.renderCalendar();
        this.hideLoader();
        await this.updateNavButtons();
        this.bindEvents();
    }
    
    
    renderSkeleton() {
        const daysInMonth = this.getDaysInMonth();
        const firstDay = this.getFirstDayOfMonth();
        
        this.calendarBody.innerHTML = '';
        
        
        for (let i = 0; i < firstDay; i++) {
            const emptyCell = document.createElement('div');
            emptyCell.className = 'calendar__cell calendar__cell--empty';
            this.calendarBody.appendChild(emptyCell);
        }
        
        
        for (let day = 1; day <= daysInMonth; day++) {
            const cell = document.createElement('div');
            cell.className = 'calendar__cell calendar__cell--skeleton';
            
            const dateEl = document.createElement('div');
            dateEl.className = 'calendar__date';
            dateEl.textContent = String(day).padStart(2, '0');
            
            cell.appendChild(dateEl);
            
            const placesEl = document.createElement('div');
            placesEl.className = 'calendar__places calendar__places--skeleton';
            cell.appendChild(placesEl);
            
            this.calendarBody.appendChild(cell);
        }
    }
    
    
    showLoader() {
        this.renderSkeleton();
        this.calendarBody.style.opacity = '1';
        this.calendarBody.style.transform = 'translateY(0)';
    }
    
    hideLoader() {
        this.calendarBody.style.opacity = '1';
        this.calendarBody.style.transform = 'translateY(0)';
    }
    
    bindEvents() {
        this.prevBtn.addEventListener('click', () => {
            if (!this.isLoading) {
                this.changeMonth(-1);
            }
        });
        this.nextBtn.addEventListener('click', () => {
            if (!this.isLoading) {
                this.changeMonth(1);
            }
        });
        
        let scrollTimeout;
        const calendarScroll = document.querySelector('.calendar-scroll');
        if (calendarScroll) {
            const forceReflow = (element) => {
                if (element) {
                    element.offsetHeight;
                }
            };
            
            const resetHoverStates = () => {
                const cells = this.calendarBody.querySelectorAll('.calendar__cell');
                cells.forEach(cell => {
                    const isHovered = cell.matches(':hover');
                    if (isHovered) {
                        forceReflow(cell);
                        cell.style.transform = 'translateZ(0)';
                        requestAnimationFrame(() => {
                            cell.style.transform = '';
                        });
                    }
                });
            };
            
            calendarScroll.addEventListener('scroll', () => {
                clearTimeout(scrollTimeout);
                resetHoverStates();
                scrollTimeout = setTimeout(() => {
                    resetHoverStates();
                }, 100);
            }, { passive: true });
        }
        
        window.addEventListener('scroll', () => {
            if (this.calendarBody) {
                const cells = this.calendarBody.querySelectorAll('.calendar__cell');
                cells.forEach(cell => {
                    if (cell.matches(':hover')) {
                        cell.style.transform = 'translateZ(0)';
                        requestAnimationFrame(() => {
                            cell.style.transform = '';
                        });
                    }
                });
            }
        }, { passive: true });
    }
    
    
    async fetchMonthData(monthKey) {
        try {
            const [year, month] = monthKey.split('-').map(Number);
            const response = await fetch(`${API_CONFIG.baseUrl}/calendar?month=${month}&year=${year}&_t=${Date.now()}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const calendarData = await response.json();
            
            const formattedData = {
                success: true,
                month: monthKey,
                data: calendarData
            };
            
            return formattedData;
        } catch (error) {
            console.error('Error fetching month data:', error);
            return { success: false, month: monthKey, data: {} };
        }
    }
    
    
    async hasMonthData(date) {
        const testDate = new Date(date);
        const monthKey = `${testDate.getFullYear()}-${String(testDate.getMonth() + 1).padStart(2, '0')}`;
        
        if (this.monthDataCache.has(monthKey)) {
            return this.monthDataCache.get(monthKey);
        }
        
        try {
            const [year, month] = monthKey.split('-').map(Number);
            const response = await fetch(`${API_CONFIG.baseUrl}/calendar?month=${month}&year=${year}&_t=${Date.now()}`);
            
            if (!response.ok) {
                this.monthDataCache.set(monthKey, false);
                return false;
            }
            
            const calendarData = await response.json();
            if (!calendarData || Object.keys(calendarData).length === 0) {
                this.monthDataCache.set(monthKey, false);
                return false;
            }
            
            for (const dateKey in calendarData) {
                const dayData = calendarData[dateKey];
                if (Array.isArray(dayData) && dayData.length > 0) {
                    const hasAvailableSlots = dayData.some(slot => {
                        return slot.available && slot.available > 0 && slot.time !== 'dayoff';
                    });
                    if (hasAvailableSlots) {
                        this.monthDataCache.set(monthKey, true);
                        return true;
                    }
                }
            }
            
            this.monthDataCache.set(monthKey, false);
            return false;
        } catch (error) {
            this.monthDataCache.set(monthKey, false);
            return false;
        }
    }
    
    
    async updateNavButtons() {
        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        const displayedMonthKey = `${this.currentDate.getFullYear()}-${String(this.currentDate.getMonth() + 1).padStart(2, '0')}`;
        
        const isDisplayedCurrentMonth = displayedMonthKey === currentMonthKey;
        
        let hasPrev = false;
        if (!isDisplayedCurrentMonth) {
            const prevDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
            const prevMonthKey = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
            const isPrevBeforeCurrent = prevMonthKey < currentMonthKey;
            if (!isPrevBeforeCurrent) {
                hasPrev = await this.hasMonthData(prevDate);
            }
        }
        
        
        const nextDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
        const hasNext = await this.hasMonthData(nextDate);
        
        
        this.prevBtn.disabled = !hasPrev;
        this.nextBtn.disabled = !hasNext;
        
        
        this.prevBtn.classList.toggle('calendar-nav__btn--disabled', !hasPrev);
        this.nextBtn.classList.toggle('calendar-nav__btn--disabled', !hasNext);
    }
    
    async changeMonth(direction) {
        if (this.isLoading) return;
        
        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        
        const newDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + direction, 1);
        const newMonthKey = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
        
        if (direction === -1 && newMonthKey < currentMonthKey) {
            return;
        }
        
        this.isLoading = true;
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        
        this.currentDate = newDate;
        this.monthText.textContent = this.getMonthName();
        
        this.calendarBody.style.transition = 'opacity 0.15s ease';
        this.calendarBody.style.opacity = '0';
        
        await new Promise(resolve => setTimeout(resolve, 150));
        
        this.showLoader();
        
        await this.renderCalendar();
        
        await this.updateNavButtons();
        
        this.calendarBody.style.transition = 'opacity 0.2s ease';
        requestAnimationFrame(() => {
            this.calendarBody.style.opacity = '1';
        });
        
        this.isLoading = false;
        this.prevBtn.disabled = false;
        this.nextBtn.disabled = false;
    }
    
    getMonthKeyForDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }
    
    getMonthKey() {
        const year = this.currentDate.getFullYear();
        const month = String(this.currentDate.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }
    
    getMonthName() {
        const monthIndex = this.currentDate.getMonth();
        const year = this.currentDate.getFullYear();
        return `${monthNames.uk[monthIndex]} ${year}`;
    }
    
    getDaysInMonth() {
        return new Date(
            this.currentDate.getFullYear(),
            this.currentDate.getMonth() + 1,
            0
        ).getDate();
    }
    
    getFirstDayOfMonth() {
        const firstDay = new Date(
            this.currentDate.getFullYear(),
            this.currentDate.getMonth(),
            1
        );
        
        return (firstDay.getDay() + 6) % 7;
    }
    
    isPastDate(day) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(
            this.currentDate.getFullYear(),
            this.currentDate.getMonth(),
            day
        );
        return checkDate < today;
    }
    
    isToday(day) {
        const today = new Date();
        const checkDate = new Date(
            this.currentDate.getFullYear(),
            this.currentDate.getMonth(),
            day
        );
        return today.getDate() === checkDate.getDate() &&
               today.getMonth() === checkDate.getMonth() &&
               today.getFullYear() === checkDate.getFullYear();
    }
    
    isWeekend(day) {
        const date = new Date(
            this.currentDate.getFullYear(),
            this.currentDate.getMonth(),
            day
        );
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6; 
    }
    
    getAvailability(day, monthData) {
        if (!monthData || !monthData.success || !monthData.data) {
            return {
                available: 0,
                status: 'no-availability',
                timeSlots: {}
            };
        }
        
        const dateKey = `${this.currentDate.getFullYear()}-${String(this.currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayData = monthData.data[dateKey];
        
        if (!dayData || !Array.isArray(dayData) || dayData.length === 0) {
            return {
                available: 0,
                status: 'no-availability',
                timeSlots: {}
            };
        }
        
        if (dayData.length === 1 && dayData[0].time === 'dayoff' && dayData[0].is_dayoff) {
            return {
                available: 0,
                status: 'dayoff',
                timeSlots: {}
            };
        }
        
        const totalAvailable = dayData.reduce((sum, slot) => sum + (slot.available || 0), 0);
        const timeSlots = {};
        
        dayData.forEach(slot => {
            if (slot.time !== 'dayoff') {
                timeSlots[slot.time] = {
                    available: slot.available,
                    total: slot.total
                };
            }
        });
        
        return {
            available: totalAvailable,
            status: totalAvailable > 0 ? 'available' : 'no-availability',
            timeSlots: timeSlots
        };
    }
    
    getPlacesText(availability) {
        if (availability.status === 'dayoff') {
            return 'Вихідний';
        }
        
        const count = availability.available;
        if (count === 0) return '0 місць';
        if (count === 1) return '1 місце';
        if (count >= 2 && count <= 4) return `${count} місця`;
        return `${count} місць`;
    }
    
    getCellClass(day, availability) {
        const classes = [];
        
        
        if (this.isToday(day)) {
            classes.push('calendar__cell--today');
        }
        
        if (!availability) {
            if (this.isPastDate(day)) {
                classes.push('calendar__cell--inactive');
            } else {
                classes.push('calendar__cell--active');
            }
            return classes.join(' ');
        }
        
        if (availability.status === 'dayoff') {
            classes.push('calendar__cell--dayoff');
            return classes.join(' ');
        }
        
        if (this.isPastDate(day)) {
            classes.push('calendar__cell--inactive');
            return classes.join(' ');
        }
        
        if (availability.available === 0) {
            classes.push('calendar__cell--no-availability');
            return classes.join(' ');
        }
        
        classes.push('calendar__cell--active');
        return classes.join(' ');
    }
    
    handleDateClick(day, availability) {
        if (this.isPastDate(day) || 
            (availability && (availability.status === 'dayoff' || availability.available === 0))) {
            return;
        }
        
        if (window.bookingForm && availability) {
            const dateKey = `${this.currentDate.getFullYear()}-${String(this.currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            
            const dateData = {
                year: this.currentDate.getFullYear(),
                month: this.currentDate.getMonth(),
                day: day,
                date: dateKey
            };
            
            window.bookingForm.open(dateData, { timeSlots: availability.timeSlots });
        }
    }
    
    async renderCalendar() {
        const daysInMonth = this.getDaysInMonth();
        const firstDay = this.getFirstDayOfMonth();
        const monthKey = this.getMonthKey();
        
        const monthData = await this.fetchMonthData(monthKey);
        
        this.calendarBody.innerHTML = '';
        
        
        for (let i = 0; i < firstDay; i++) {
            const emptyCell = document.createElement('div');
            emptyCell.className = 'calendar__cell calendar__cell--empty';
            this.calendarBody.appendChild(emptyCell);
        }
        
        
        for (let day = 1; day <= daysInMonth; day++) {
            const availability = this.getAvailability(day, monthData);
            const cell = document.createElement('div');
            const cellClass = this.getCellClass(day, availability);
            
            cell.className = `calendar__cell ${cellClass}`;
            
            const dateEl = document.createElement('div');
            dateEl.className = 'calendar__date';
            dateEl.textContent = String(day).padStart(2, '0');
            
            cell.appendChild(dateEl);
            
            
            if (this.isToday(day)) {
                const triangleEl = document.createElement('span');
                triangleEl.className = 'calendar__today-triangle';
                cell.appendChild(triangleEl);
            }
            
            
            if (cell.classList.contains('calendar__cell--active')) {
                const hoverBgEl = document.createElement('div');
                hoverBgEl.className = 'calendar__hover-bg';
                cell.appendChild(hoverBgEl);
            }
            
            
            if (!this.isPastDate(day)) {
                const placesEl = document.createElement('div');
                placesEl.className = 'calendar__places';
                
                if (availability) {
                    placesEl.textContent = this.getPlacesText(availability);
                } else if (this.isWeekend(day)) {
                    placesEl.textContent = 'Вихідний';
                    if (!cell.classList.contains('calendar__cell--dayoff')) {
                        cell.classList.add('calendar__cell--dayoff');
                    }
                } else {
                    placesEl.textContent = 'Доступно';
                }
                
                if (placesEl.textContent) {
                    cell.appendChild(placesEl);
                }
            }
            
            
            if (!this.isPastDate(day) && 
                (!availability || (availability.status !== 'dayoff' && availability.available > 0))) {
                cell.addEventListener('click', () => this.handleDateClick(day, availability));
            }
            
            cell.addEventListener('mouseleave', () => {
                requestAnimationFrame(() => {
                    cell.style.transform = 'translateZ(0)';
                    requestAnimationFrame(() => {
                        cell.style.transform = '';
                    });
                });
            });
            
            this.calendarBody.appendChild(cell);
        }
    }
}

export default function initBooking() {
    if (document.getElementById('calendarBody')) {
        new BookingCalendar();
        
        const calendarScroll = document.querySelector('.calendar-scroll');
        if (calendarScroll) {
            const initSimpleBar = () => {
                const existingInstance = SimpleBar.instances.get(calendarScroll);
                
                if (!existingInstance) {
                    new SimpleBar(calendarScroll, {
                        direction: 'horizontal',
                        autoHide: false,
                        scrollbarMinSize: 50,
                        clickOnTrack: true,
                    });
                } else {
                    existingInstance.unMount();
                }
            };
            
            initSimpleBar();
        }
    }
}
